import { CustomFieldService } from '../services/user-fields.service';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import * as customFieldsActions from 'app/actions/custom-fields';
import {
  CustomFieldCategory,
  CustomFieldResponse,
  CustomFieldsCategoriesResponse,
  CustomFieldsPagedResponse,
  CustomFieldsTypeCategory,
  SaveOrUpdateCategoryRequest
} from 'app/models/api/custom-fields';
import * as fromRouter from '../reducers/router';
import { Router } from '@angular/router';
import * as fromCustomFields from '../reducers/custom-fields';
import * as fromProfile from '../reducers/profile';
import { CustomFieldsEffectsUtils } from './utils/custom-fields-effects.utils';
import { TranslateService } from '@ngx-translate/core';
import {
  UserDefinedFieldsCategoriesUtils
} from '../components/user-defined-fields/lib/utils/user-defined-fields-categories.utils';
import { initialMetaData } from '../models/reducers/meta-data';
import { ApplicationStore } from 'app/state-management/application/application.store';

@Injectable()
export class CustomFieldsEffects {
  private readonly store: Store = inject(Store);
  private readonly router: Router = inject(Router);
  private readonly actions$: Actions = inject(Actions);
  private readonly customFieldsService: CustomFieldService = inject(CustomFieldService);
  private readonly translateService: TranslateService = inject(TranslateService);
  private readonly appStore: ApplicationStore = inject(ApplicationStore);

  load$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(customFieldsActions.loadCustomFields),
    mergeMap(action => this.customFieldsService.getCustomFields(action.customFieldType).pipe(
      map(customFields => customFieldsActions.loadCustomFieldsSuccess({
        customFieldType: action.customFieldType,
        customFields,
        renewData: false
      })),
      catchError(error => of(customFieldsActions.loadCustomFieldsFail({
        customFieldType: action.customFieldType,
        error
      })))
    ))
  ));

  loadPagedCustomFields$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(customFieldsActions.loadPagedCustomFields),
    filter(action => !!action.customFieldType),
    concatLatestFrom(() => [
      this.store.select(fromCustomFields.getCustomFieldOrderBasedOnRoute),
      this.store.select(fromCustomFields.getCustomFieldPaginationBasedOnRoute),
      this.store.select(fromCustomFields.selectSelectedCategories)
    ]),
    switchMap(([action, order, paginationOptions, selectedFields]) => this.customFieldsService.getPagedCustomFields(
      action.customFieldType,
      CustomFieldsEffectsUtils.buildBasePagedRequestPayload(order, selectedFields, action.resetPagination ? initialMetaData.paginationOptions : paginationOptions)
    ).pipe(
      map((response: CustomFieldsPagedResponse) => customFieldsActions.loadPagedCustomFieldsSuccess({
        customFieldType: action.customFieldType,
        customFields: response.items,
        response,
        renewData: action.resetPagination
      })),
      catchError(error => of(customFieldsActions.loadPagedCustomFieldsFail({
        customFieldType: action.customFieldType,
        error
      })))
    ))
  ));

  selectCategory$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(customFieldsActions.selectCategory),
      map(action => customFieldsActions.loadPagedCustomFields({
        customFieldType: action.customFieldCategory,
        resetPagination: true
      }))
    );
  });

  loadPagedCustomFieldsSuccess$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(customFieldsActions.loadPagedCustomFieldsSuccess),
      filter(action => !action.renewData),
      map(action => customFieldsActions.loadCustomFieldsCategoriesByType({ customFieldType: action.customFieldType }))
    );
  });

  loadRepairTaskCategories$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(customFieldsActions.loadRepairTaskCategories),
    switchMap(() => this.customFieldsService.getCustomFieldsCategoriesByType(
      CustomFieldCategory.repairTasks
    ).pipe(
      map((response: CustomFieldsCategoriesResponse) => customFieldsActions.loadRepairTaskCategoriesSuccess({
        customFieldsCategories: response?.categories || [],
        customFieldsTranslatedLanguages: response?.translatedLanguages || []
      })),
      catchError(error => of(customFieldsActions.loadRepairTaskCategoriesFail({ error })))
    ))
  ));

  loadCustomFieldsCategoriesByType$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(customFieldsActions.loadCustomFieldsCategoriesByType),
    filter(action => !!action.customFieldType),
    switchMap((action) => this.customFieldsService.getCustomFieldsCategoriesByType(
      action.customFieldType
    ).pipe(
      map((response: CustomFieldsCategoriesResponse) => customFieldsActions.loadCustomFieldsCategoriesByTypeSuccess({
        customFieldsCategories: response?.categories || [],
        customFieldsTranslatedLanguages: response?.translatedLanguages || []
      })),
      catchError(error => of(customFieldsActions.loadCustomFieldsCategoriesByTypeFail({
        customFieldType: action.customFieldType,
        error
      })))
    ))
  ));

  saveOrUpdateCategory$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(customFieldsActions.saveOrUpdateCategory),
    filter(action => !!action.customFieldCategory),
    withLatestFrom(this.store.select(fromProfile.selectProfile)),
    switchMap(([action, profile]) => {
      const addOrUpdateModel: SaveOrUpdateCategoryRequest = UserDefinedFieldsCategoriesUtils.buildSaveOrUpdateCategoryModel(action.category, profile.name);
      return this.customFieldsService.saveOrUpdateCategory(
        action.customFieldCategory,
        addOrUpdateModel
      ).pipe(
        map((category: CustomFieldsTypeCategory) => customFieldsActions.saveOrUpdateCategorySuccess({
          text: 'USER_DEFINED_FIELDS.CATEGORIES.UPDATE_SUCCESS',
          oldCategory: action.category,
          category
        })),
        catchError(error => of(customFieldsActions.saveOrUpdateCategoryFail({ error })))
      );
    })
  ));

  saveCategoriesTranslations$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(customFieldsActions.saveCategoriesTranslations),
    filter(action => !!action.target),
    switchMap((action) => {
      return this.customFieldsService.saveCategoriesTranslations(
        action.target,
        action.data
      ).pipe(
        map((response: CustomFieldsCategoriesResponse) => customFieldsActions.saveCategoriesTranslationsSuccess({ response })),
        catchError(error => of(customFieldsActions.saveCategoriesTranslationsFail({ error })))
      );
    })
  ));

  deleteCategory$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(customFieldsActions.deleteCategory),
    filter(action => !!action.customFieldCategory),
    switchMap((action) => {
      return this.customFieldsService.deleteCategory(
        action.customFieldCategory,
        action.category?.id
      ).pipe(
        map(() => customFieldsActions.deleteCategorySuccess({
          text: 'USER_DEFINED_FIELDS.CATEGORIES.DELETE_SUCCESS',
          category: action.category
        })),
        catchError(error => of(customFieldsActions.deleteCategoryFail({ error })))
      );
    })
  ));

  assignCategories$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(customFieldsActions.assignCategories),
    filter(action => !!action.requestModel),
    switchMap((action) => {
      return this.customFieldsService.assignCategories(
        action.requestModel.type,
        { categoryCustomFieldMapping: action.requestModel.categoryCustomFieldMapping }
      ).pipe(
        map((customFields) => customFieldsActions.assignCategoriesSuccess({
          text: 'USER_DEFINED_FIELDS.CATEGORIES.ASSIGN_SUCCESS',
          customFields,
          customFieldsCategory: action.requestModel.type
        })),
        catchError(error => of(customFieldsActions.assignCategoriesFail({ error })))
      );
    })
  ));

  orderPagedCustomFields$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(
      customFieldsActions.orderCustomFieldsBy,
      customFieldsActions.changeCustomFieldsPageByCategory,
      customFieldsActions.changeCustomFieldsPageSizeByCategory
    ),
    concatLatestFrom(() => [
      this.store.select(fromRouter.getCustomFieldCategory)
    ]),
    switchMap(([, customFieldType]) => [customFieldsActions.loadPagedCustomFields({ customFieldType })])
  ));

  changeOption$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        customFieldsActions.createCustomFieldSuccess,
        customFieldsActions.deleteCustomFieldSuccess
      ),
      map(action => customFieldsActions.loadCustomFields({ customFieldType: action.customFieldType }))
    );
  });

  create$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(customFieldsActions.createCustomField),
      switchMap(action => {
        return this.customFieldsService.createCustomField(action.customFieldType, action.customField).pipe(
          map(() => customFieldsActions.createCustomFieldSuccess({ customFieldType: action.customFieldType })),
          catchError(error => of(customFieldsActions.createCustomFieldFail({
            customFieldType: action.customFieldType, error
          })))
        );
      })
    );
  });

  onCreateSuccess$ = createEffect(() => this.actions$.pipe(
    ofType(customFieldsActions.createCustomFieldSuccess),
    map(action => action.customFieldType),
    tap((customFieldType: CustomFieldCategory) => {
      this.router.navigate([
        '/administration',
        'user-defined-fields',
        customFieldType
      ]);
    })
  ), { dispatch: false });

  update$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(customFieldsActions.updateCustomField),
      switchMap(action => {
        return this.customFieldsService.editCustomField(action.customFieldType, action.customField, action.id).pipe(
          map(() => customFieldsActions.updateCustomFieldSuccess({ customFieldType: action.customFieldType })),
          catchError(error => of(customFieldsActions.updateCustomFieldFail({
            customFieldType: action.customFieldType, error
          })))
        );
      })
    );
  });

  getCustomFieldDetails$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(customFieldsActions.getCustomFieldDetails),
      concatLatestFrom(() => [
        this.store.select(fromRouter.getCustomFieldId),
        this.store.select(fromRouter.getCustomFieldCategory)
      ]),
      switchMap(([, fieldId, fieldCategory]) => {
        return this.customFieldsService.getCustomFieldDetails(fieldId, fieldCategory).pipe(
          switchMap((response: CustomFieldResponse) => [
            customFieldsActions.getCustomFieldDetailsSuccess({ field: response }),
            customFieldsActions.loadCustomFieldsCategoriesByType({ customFieldType: fieldCategory })
          ]),
          catchError(error => of(customFieldsActions.getCustomFieldDetailsFail({ error })))
        );
      })
    );
  });

  getCustomFieldDetailsFail$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(customFieldsActions.getCustomFieldDetailsFail),
      tap(() => {
        this.router.navigate(['/administration', 'user-defined-fields']);
      })
    );
  }, { dispatch: false });

  delete$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(customFieldsActions.deleteCustomField),
      switchMap(action => {
        return this.customFieldsService.deleteCustomField(action.customFieldType, action.id).pipe(
          map(() => customFieldsActions.deleteCustomFieldSuccess({ customFieldType: action.customFieldType })),
          catchError(error => of(customFieldsActions.deleteCustomFieldFail({
            customFieldType: action.customFieldType, error
          })))
        );
      })
    );
  });

  showSuccessSnack$: Observable<unknown> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          customFieldsActions.saveOrUpdateCategorySuccess,
          customFieldsActions.assignCategoriesSuccess,
          customFieldsActions.deleteCategorySuccess
        ),
        tap((action) => this.appStore.showSnack({ text: this.translateService.instant(action.text), type: 'info' }))
      ),
    { dispatch: false }
  );

  showErrorSnack$: Observable<unknown> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          customFieldsActions.saveOrUpdateCategoryFail,
          customFieldsActions.assignCategoriesFail,
          customFieldsActions.deleteCategoryFail
        ),
        tap(() => this.appStore.showSnack({
          text: this.translateService.instant('MISC.DATA_NOT_UPDATED'),
          type: 'error'
        }))
      ),
    { dispatch: false }
  );
}
